import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  TabContent,
  TabPane,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  CustomInput,
} from 'reactstrap';
import {
  ContAffiliation,
  H5Styled,
  ContFilterYear,
} from '../../../styled-components';
import Summary from './Summary';
import TotalRevenuesRow from './TotalTrRevenue';
import TotalEnergyRevenuesRow from './EnergyDetails/TotalTrEnergyRevenue';
import TotalRecurrentEnergyRevenuesRow from './RecurrentEnergyDetails/TotalTrRecurrentEnergyRevenue';
import TotalInsuranceRevenuesRow from './TotalTrInsuranceRevenue';
import RoyaltyTable from './RoyaltyTable';
import InvoiceBonusTable from './InvoiceBonusTable';
import { useAuth } from '../../../commons/Auth';
import generateYearOptions from '../../../lib/helpers/generateYearOptions';
import InsurancesRoyaltyTable from './InsurancesRoyaltyTable';
import EnergyRoyaltyTable from './EnergyDetails/EnergyRoyaltyTable';
import RecurrentEnergyRoyaltyTable from './RecurrentEnergyDetails/RecurrentEnergyRoyaltyTable';
import { SIGNALS_TYPES, ROYALTIES_ALLOWED_TYPES } from '../../../App';
import SignalsRoyaltyTable from './SignalsRoyaltyTable';
import TotalSignalsRevenuesRow from './TotalSignalsRevenuesRow';

const FinancialReportsPresentational = ({
  data = {},
  onBonusDelete,
  onBonusUpdate,
  query,
  onFilter,
  profileType,
  addPaymentDate,
  profile,
}) => {
  const [{ newIsAdmin } = {}] = useAuth();
  const location = useLocation();
  const [t] = useTranslation('CONTACTS');
  const { categories = {}, totals = {} } = data;
  const {
    gnet_admin = {},
    supplier = {},
    bonuses = {},
    variableRevenuesSubAdmins = {},
    variableRevenues = {},
    variableRevenuesInsurance = {},
    variableRevenuesEnergy = {},
    variableRevenuesRecurrentEnergy = {},
    variableRevenuesSignals = {},
  } = categories;

  const { data: dataRevSubAdmins = [] } = variableRevenuesSubAdmins;
  const { data: dataRev = [] } = variableRevenues;
  const { data: dataRevInsurances = [] } = variableRevenuesInsurance;
  const { data: dataRevEnergy = [] } = variableRevenuesEnergy;
  const { data: dataRevRecurrentEnergy = [] } = variableRevenuesRecurrentEnergy;
  const { data: dataRevSignals = [] } = variableRevenuesSignals;

  useEffect(() => {
    if (location.hash !== '#iaf') return;

    document.getElementById('iaf')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [location.hash, data]);

  const getVariableRevenuesTotals = (dataRev) => {
    const cashedTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.paidAmount || 0,
      0
    );
    const netTotals = dataRev.reduce((prev, cur) => prev + cur?.net || 0, 0);
    const grossTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.gross || 0,
      0
    );
    const extraShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.extraShare,
      0
    );
    const adminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.adminShare,
      0
    );
    const earnedAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.earnedAdminShare,
      0
    );
    const billableAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.billableAdminShare,
      0
    );
    return {
      cashedTotals,
      netTotals,
      grossTotals,
      extraShareTotals,
      adminShareTotals,
      earnedAdminShareTotals,
      billableAdminShareTotals,
    };
  };

  const getVariableRevenuesInsuranceTotals = (dataRev) => {
    const netTotals = dataRev.reduce((prev, cur) => prev + cur?.net || 0, 0);
    const grossTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.gross || 0,
      0
    );
    const shareToInvoiceTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.shareToInvoice || 0,
      0
    );
    const panoramicaShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.panoramicaShare,
      0
    );
    const adminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.adminShare,
      0
    );
    const earnedAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.earnedAdminShare,
      0
    );
    const billableAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.billableAdminShare,
      0
    );
    return {
      netTotals,
      grossTotals,
      shareToInvoiceTotals,
      panoramicaShareTotals,
      adminShareTotals,
      earnedAdminShareTotals,
      billableAdminShareTotals,
    };
  };

  const getVariableRevenuesEnergyTotals = (dataRev) => {
    const cashedTotals = dataRev.reduce((prev, cur) => prev + cur.gross, 0);
    const netTotals = dataRev.reduce((prev, cur) => prev + cur?.net, 0);
    const grossTotals = dataRev.reduce((prev, cur) => prev + cur?.gross, 0);
    const contractValueTotals = dataRev.reduce(
      (prev, cur) => prev + cur.contract_value,
      0
    );
    const extraShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.extraShare,
      0
    );
    const adminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.adminShare,
      0
    );
    const earnedAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.earnedAdminShare,
      0
    );
    const billableAdminShareTotals = dataRev.reduce((prev, cur) => {
      return prev + cur?.billableAdminShare;
    }, 0);
    const rateableTotals = dataRev.reduce(
      (prev, cur) => prev + cur.rateable,
      0
    );

    return {
      cashedTotals,
      netTotals,
      grossTotals,
      extraShareTotals,
      rateableTotals,
      contractValueTotals,
      adminShareTotals,
      earnedAdminShareTotals,
      billableAdminShareTotals,
    };
  };

  const getVariableRevenuesSignalsTotals = (dataRev) => {
    const estimateTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.estimate || 0,
      0
    );
    const extraShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.extraShare,
      0
    );
    const adminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.adminShare,
      0
    );
    const earnedAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur?.earnedAdminShare,
      0
    );
    const billableAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.billableAdminShare,
      0
    );
    const iafsTotals = dataRev.reduce((prev, cur) => {
      const { iaf = {} } = cur;
      const iafAmount = iaf.amount || 0;
      return prev + iafAmount;
    }, 0);

    return {
      estimateTotals,
      extraShareTotals,
      adminShareTotals,
      earnedAdminShareTotals,
      billableAdminShareTotals,
      iafsTotals,
    };
  };

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <TabContent activeTab={'1'}>
        <TabPane tabId="1">
          <ContFilterYear>
            <Row>
              <Col xs="6">
                <Label>{t('TITLE_FILTER_YEAR')}</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="selectYear"
                    name="year"
                    value={query && query.year}
                    onChange={({ target: { value: year } }) =>
                      onFilter({ year })
                    }
                  >
                    {generateYearOptions()}
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
          {[
            ...ROYALTIES_ALLOWED_TYPES,
            'UTILIZER',
            'COMMERCIAL',
            'ASSOCIATION',
            'ADMIN_COLLAB'
          ].includes(profileType) && (
            <>
              <ContAffiliation>
                <Row>
                  <Col xs="6">
                    <H5Styled>{t('ROYALTY:TITLE')}</H5Styled>
                  </Col>
                  <Col xs="6" className="text-right" />
                </Row>
                <br />
                <br />
                <RoyaltyTable
                  data={[]}
                  profile={profile}
                  truncateTable={true}
                  onFilter={() => {}}
                  bottomRow={
                    <TotalRevenuesRow
                      totals={getVariableRevenuesTotals(dataRev)}
                      route={`variable-revenues${
                        query.year ? '?year=' + query.year : ''
                      }`}
                      activeRoute={true}
                      truncateTotals={true}
                      profileType={profileType}
                    />
                  }
                  showEmptyTableMessage={false}
                />
              </ContAffiliation>

              <ContAffiliation>
                <Row>
                  <Col xs="6">
                    <H5Styled>{t('ENERGY:ROYALTIES_TITLE')}</H5Styled>
                  </Col>
                  <Col xs="6" className="text-right" />
                </Row>
                <br />
                <br />
                <EnergyRoyaltyTable
                  data={[]}
                  profile={profile}
                  truncateTable={true}
                  onFilter={() => {}}
                  showEmptyTableMessage={false}
                  bottomRow={
                    <TotalEnergyRevenuesRow
                      totals={getVariableRevenuesEnergyTotals(dataRevEnergy)}
                      route={`variable-revenues-energy${
                        query.year ? '?year=' + query.year : ''
                      }`}
                      activeRoute={true}
                      truncateTotals={true}
                      profileType={profileType}
                    />
                  }
                />
              </ContAffiliation>

              <ContAffiliation>
                <Row>
                  <Col xs="6">
                    <H5Styled>{t('ENERGY_RECURRENT:ROYALTIES_TITLE')}</H5Styled>
                  </Col>
                  <Col xs="6" className="text-right" />
                </Row>
                <br />
                <br />
                <RecurrentEnergyRoyaltyTable
                  data={[]}
                  profile={profile}
                  truncateTable={true}
                  onFilter={() => {}}
                  showEmptyTableMessage={false}
                  bottomRow={
                    <TotalRecurrentEnergyRevenuesRow
                      totals={getVariableRevenuesEnergyTotals(
                        dataRevRecurrentEnergy
                      )}
                      route={`variable-revenues-recurrent-energy${
                        query.year ? '?year=' + query.year : ''
                      }`}
                      activeRoute={true}
                      truncateTotals={true}
                      profileType={profileType}
                    />
                  }
                />
              </ContAffiliation>

              {newIsAdmin && (
                <ContAffiliation>
                  <Row>
                    <Col xs="6">
                      <H5Styled>{t('INSURANCES:ROYALTIES_TITLE')}</H5Styled>
                    </Col>
                    <Col xs="6" className="text-right" />
                  </Row>
                  <br />
                  <br />
                  <InsurancesRoyaltyTable
                    data={[]}
                    truncateTable={true}
                    onFilter={() => {}}
                    showEmptyTableMessage={false}
                    bottomRow={
                      <TotalInsuranceRevenuesRow
                        totals={getVariableRevenuesInsuranceTotals(
                          dataRevInsurances
                        )}
                        route={`variable-revenues-insurances${
                          query.year ? '?year=' + query.year : ''
                        }`}
                        activeRoute={true}
                        truncateTotals={true}
                        profileType={profileType}
                      />
                    }
                  />
                </ContAffiliation>
              )}
            </>
          )}

          {([...SIGNALS_TYPES].includes(profileType) || profile.superCollab === true) && (
            <ContAffiliation>
              <Row>
                <Col xs="6">
                  <H5Styled>{t('SIGNALS:ROYALTIES_TITLE')}</H5Styled>
                </Col>
                <Col xs="6" className="text-right" />
              </Row>
              <br />
              <br />
              <SignalsRoyaltyTable
                data={[]}
                truncateTable={true}
                onFilter={() => {}}
                showEmptyTableMessage={false}
                bottomRow={
                  <TotalSignalsRevenuesRow
                    totals={getVariableRevenuesSignalsTotals(dataRevSignals)}
                    route={`variable-revenues-signals${
                      query.year ? '?year=' + query.year : ''
                    }`}
                    activeRoute={true}
                    truncateTotals={true}
                    profileType={profileType}
                  />
                }
              />
            </ContAffiliation>
          )}

          {profile.type !== 'UTILIZER' && (
            <ContAffiliation id="iaf">
              <Row>
                <Col xs="6">
                  <H5Styled>{t('LABEL_INVOICE_BONUS')}</H5Styled>
                </Col>
              </Row>
              <br />
              <InvoiceBonusTable
                data={bonuses.data || []}
                handleDeleteBonus={onBonusDelete}
                handleUpdateBonus={onBonusUpdate}
                newIsAdmin={newIsAdmin}
                onFilter={() => {}}
                addPaymentDate={addPaymentDate}
              />
            </ContAffiliation>
          )}

          <ContAffiliation>
            <H5Styled style={{ marginBottom: '5px' }}>
              {t('COMMON:SUMMARY')}
            </H5Styled>
            <Summary totals={totals} profileType={profileType} />
          </ContAffiliation>
        </TabPane>
      </TabContent>
    </Form>
  );
};

export default FinancialReportsPresentational;
